/**
 * ItemChecklist - Item verification checklist for packing
 *
 * Displays items to verify during packing with:
 * - Large touch-friendly rows (h-16, 64px)
 * - Clear visual states: pending (gray), verified (green), issue (red)
 * - Report Issue button for P&P items
 * - Proper touch targets for glove use (44x44px)
 *
 * CRITICAL: Follows warehouse UX requirements for touch targets.
 */
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Circle, AlertTriangle, RotateCcw, Loader2, Plus, Clock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { ORDER_CHANGE_BACKFILL_REASON } from '@/features/production-issues/types';
import { CLIENT_QC_REDO_REASON } from '@/features/production-tracker/lib/client-qc-redo';
import { type ProductionPreviewKind } from '@/components/production/ProductionPreviewTile';
import { PackingPreviewThumbnails } from './PackingPreviewThumbnails';

export interface PackingItemResult {
  garmentUnitId: string;
  sku: string;
  productName: string;
  variantTitle: string;
  color: string | null;
  size: string | null;
  isPod: boolean;
  isPap: boolean;
  status: string;
  verifiedAt: string | null;
  isVerified: boolean;
  isRedo: boolean;
  redoReason: string | null;
  imageUrl: string | null;
  /** True when this line carries the customer's own design rather than a
   *  catalogue variant. It picks the PREVIEW POLICY, not just a picture: a
   *  custom is ONE combined tile, a standard is a tile per side that has a
   *  garment mockup on file. */
  isCustomProduct: boolean;
  /** Resolved previews. Custom garments carry the signed re-hosted design plus
   *  its original source; standard garments carry the garment mockup per side.
   *  A side with no mockup gets no tile: the row shows what there is a picture
   *  of, never a stand-in for the other side. */
  customPreviewUrl: string | null;
  customPreviewSourceUrl: string | null;
  /** Signed BACK preview mockup of a custom line, when the client sends one.
   *  With it the row shows Front and Back tiles instead of one combined tile. */
  customBackPreviewUrl?: string | null;
  /** The design shown ON the garment, front. What the packer checks against. */
  frontMockupPreviewUrl: string | null;
  /** The design shown ON the garment, back. Never the front mockup. */
  backMockupPreviewUrl: string | null;
  /** The bare front print artwork file. Supplied, not rendered here. */
  frontPreviewUrl: string | null;
  /** The bare back print artwork file. Supplied, not rendered here. */
  backPreviewUrl: string | null;
  hasFront: boolean;
  hasBack: boolean;
  /** BLU-695 — a PENDING unit still in production (e.g. an inbound top-up).
   *  Renders disabled and unverifiable; blocks completion server-side. */
  notYetMade: boolean;
}

interface ItemChecklistProps {
  items: PackingItemResult[];
  onVerify: (garmentUnitId: string) => void;
  onReportIssue: (item: PackingItemResult) => void;
  /** Opens the order-wide lightbox at this garment's tile, on the source the
   *  tile actually rendered (a tile that fell past a broken first choice must
   *  not reopen it full screen). */
  onOpenPreview?: (
    garmentUnitId: string,
    kind: ProductionPreviewKind,
    sourceIndex: number,
  ) => void;
  isVerifyingId?: string | null;
}

export function ItemChecklist({
  items,
  onVerify,
  onReportIssue,
  onOpenPreview,
  isVerifyingId,
}: ItemChecklistProps) {
  const { t } = useTranslation('production');

  if (items.length === 0) {
    return (
      <div className="py-8 text-center text-muted-foreground">
        {t('packer.noItemsToVerify')}
      </div>
    );
  }

  return (
    <div className="divide-y border rounded-lg overflow-hidden">
      {items.map((item) => (
        <ItemRow
          key={item.garmentUnitId}
          item={item}
          onVerify={onVerify}
          onReportIssue={onReportIssue}
          onOpenPreview={onOpenPreview}
          isVerifyingId={isVerifyingId}
        />
      ))}
    </div>
  );
}

interface ItemRowProps {
  item: PackingItemResult;
  onVerify: (garmentUnitId: string) => void;
  onReportIssue: (item: PackingItemResult) => void;
  onOpenPreview?: (
    garmentUnitId: string,
    kind: ProductionPreviewKind,
    sourceIndex: number,
  ) => void;
  isVerifyingId?: string | null;
}

function ItemRow({ item, onVerify, onReportIssue, onOpenPreview, isVerifyingId }: ItemRowProps) {
  const { t } = useTranslation('production');
  const hasIssue = item.status === 'ISSUE' || item.status === 'DAMAGED';

  const isDisabledRow = item.notYetMade || hasIssue;

  return (
    <div
      className={cn(
        // CRITICAL: h-16 (64px) minimum height for glove-friendly touch.
        // py-5: the 58px tile's out-of-flow caption needs 20px below the
        // frame, and the frame then centres at 49 of 98.
        'flex items-center gap-4 min-h-16 py-5 px-4 transition-colors',
        item.isVerified && 'bg-green-50/50 dark:bg-green-950/20',
        hasIssue && 'bg-red-50/50 dark:bg-red-950/20 border-l-4 border-l-red-500',
        item.notYetMade && 'bg-muted/40',
        !item.isVerified && !isDisabledRow && 'hover:bg-muted/50'
      )}
    >
      {/* Checkbox area - 56x56 touch target for glove use */}
      <button
        type="button"
        role="checkbox"
        aria-checked={item.isVerified}
        aria-busy={isVerifyingId === item.garmentUnitId}
        onClick={() => !item.isVerified && !isDisabledRow && onVerify(item.garmentUnitId)}
        disabled={item.isVerified || isDisabledRow || isVerifyingId === item.garmentUnitId}
        className={cn(
          // CRITICAL: 56x56px touch target for warehouse gloves
          'flex items-center justify-center w-14 h-14 rounded-lg transition-colors',
          !item.isVerified && !isDisabledRow && 'hover:bg-muted active:bg-muted/80',
          (item.isVerified || isDisabledRow || isVerifyingId === item.garmentUnitId) && 'cursor-default'
        )}
        aria-label={
          item.isVerified
            ? t('packer.verified')
            : hasIssue
              ? t('packer.hasIssue')
              : item.notYetMade
                ? t('packer.notYetMade')
                : t('packer.clickToVerify')
        }
      >
        {item.isVerified ? (
          <CheckCircle2 className="w-8 h-8 text-green-600 animate-check-enter" aria-hidden="true" />
        ) : hasIssue ? (
          <AlertTriangle className="w-8 h-8 text-red-600" aria-hidden="true" />
        ) : item.notYetMade ? (
          <Clock className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
        ) : isVerifyingId === item.garmentUnitId ? (
          <Loader2 className="w-8 h-8 text-muted-foreground animate-spin" aria-hidden="true" />
        ) : (
          <Circle className="w-8 h-8 text-muted-foreground" aria-hidden="true" />
        )}
      </button>

      {/* Product previews — one tile per side that has a garment mockup, or
          the customer's own preview for a custom garment. Falls back to the
          catalogue thumbnail, and states the exhaustion when there is no
          picture at all rather than dropping the column out of the row. */}
      <PackingPreviewThumbnails
        item={item}
        productName={item.productName}
        onOpenPreview={
          onOpenPreview
            ? (kind, sourceIndex) => onOpenPreview(item.garmentUnitId, kind, sourceIndex)
            : undefined
        }
      />

      {/* Item info - flex-1 */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'font-medium text-base truncate',
            (hasIssue || item.notYetMade) && 'text-muted-foreground',
            hasIssue && 'line-through'
          )}
        >
          {item.productName}
        </p>
        <div className="flex gap-2 mt-1 flex-wrap">
          <Badge variant="outline" className="text-xs font-mono">
            {item.sku}
          </Badge>
          {item.color && (
            <Badge variant="secondary" className="text-xs">
              {item.color}
            </Badge>
          )}
          {item.size && (
            <Badge variant="secondary" className="text-xs">
              {item.size}
            </Badge>
          )}
          {item.notYetMade && (
            <Badge
              variant="outline"
              className="text-xs border-muted-foreground/40 text-muted-foreground flex items-center gap-1"
            >
              <Clock className="h-3 w-3" aria-hidden="true" />
              {t('packer.notYetMade')}
            </Badge>
          )}
          {item.isRedo && (
            item.redoReason === ORDER_CHANGE_BACKFILL_REASON ? (
              <Badge
                variant="outline"
                className="text-xs border-orange-400 text-orange-700 dark:border-orange-600 dark:text-orange-300 flex items-center gap-1"
              >
                <Plus className="h-3 w-3" aria-hidden="true" />
                {t('orderChange.badge')}
              </Badge>
            ) : (
              <Badge
                variant="outline"
                className="text-xs border-amber-400 text-amber-700 dark:border-amber-600 dark:text-amber-300 flex items-center gap-1"
                title={t('packer.replacementFor')}
              >
                <RotateCcw className="h-3 w-3" aria-hidden="true" />
                {/* BLU-1462: a client QC redo goes back to the hand over bench. */}
                {item.redoReason === CLIENT_QC_REDO_REASON ? t('clientQcRedo.badge') : 'REDO'}
              </Badge>
            )
          )}
          {item.isPap && (
            <Badge className="text-xs bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300">
              P&P
            </Badge>
          )}
          {item.isPod && (
            <Badge className="text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300">
              POD
            </Badge>
          )}
        </div>
      </div>

      {/* Report Issue button - only for P&P items that aren't verified (and
          have actually been made — nothing to report on a not-yet-made unit) */}
      {item.isPap && !item.isVerified && !hasIssue && !item.notYetMade && (
        <Button
          variant="destructive"
          size="default"
          // h-14 (56px) for glove-friendly touch target
          className="h-14 px-4 flex-shrink-0"
          onClick={() => onReportIssue(item)}
          disabled={isVerifyingId === item.garmentUnitId}
        >
          <AlertTriangle className="w-5 h-5 mr-2" aria-hidden="true" />
          {t('packer.issue')}
        </Button>
      )}
    </div>
  );
}
